import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Phone, HeartHandshake, X, Check } from "lucide-react";

function telHref(number) {
  const digits = (number || "").replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : null;
}

export default function CrisisSupportCard({ resources, onDismiss, anonymous = false, onSignup }) {
  const [notifying, setNotifying] = useState(false);
  const [notified, setNotified] = useState(false);
  const hotlines = resources?.hotlines || [];

  const notifyContact = async () => {
    setNotifying(true);
    try {
      const { data } = await api.post("/alert/notify-contact", { reason: "crisis" });
      if (data.ok) {
        setNotified(true);
        toast.success(
          data.contact_name ? `I've reached out to ${data.contact_name}. You're not alone in this.` : "I've reached out to your trusted person.",
          { duration: 6000 }
        );
      } else {
        toast.error("I couldn't reach your contact just now. Please call them directly if you can.");
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("I reached out to them very recently. If you need someone right now, please call one of the numbers above.", { duration: 9000 });
      } else if (status === 400) {
        toast.error("Add a trusted contact in your profile so I can reach them — or please call one of the numbers above now.", { duration: 9000 });
      } else {
        toast.error("I couldn't reach your contact just now. Please call one of the numbers above, or reach them directly.", { duration: 9000 });
      }
    } finally {
      setNotifying(false);
    }
  };

  return (
    <div
      data-testid="crisis-support-card"
      className="relative rounded-3xl border-2 border-[#2E9D96]/40 bg-white shadow-md overflow-hidden"
    >
      <div className="h-1.5 bg-[#2E9D96]" />
      <button
        onClick={onDismiss}
        className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 transition-colors"
        aria-label="Dismiss"
        data-testid="crisis-dismiss"
      >
        <X size={18} />
      </button>
      <div className="p-6 md:p-7">
        <div className="flex items-center gap-2">
          <HeartHandshake className="text-[#2E9D96]" size={20} />
          <span className="text-xs uppercase tracking-[0.22em] text-[#2E9D96] font-semibold">You matter — right now</span>
        </div>
        <h3 className="font-display text-2xl text-slate-800 mt-2">I'm right here with you.</h3>
        <p className="text-slate-600 mt-1.5 text-[15px] leading-relaxed">
          What you're feeling is real, and you don't have to carry it alone this second. If things feel like
          too much, please reach out to someone who can be with you.
          {resources?.note ? ` ${resources.note}` : ""}
        </p>

        {hotlines.length > 0 && (
          <div className="mt-5" data-testid="crisis-hotlines">
            <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2.5">
              {resources?.region ? `${resources.region} — reach a real person now` : "Reach a real person now"}
            </p>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {hotlines.map((h) => {
                const href = telHref(h.number);
                const inner = (
                  <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-[#F4F7F6] px-4 py-3 hover:border-[#2E9D96]/50 transition-colors">
                    <div className="w-9 h-9 rounded-xl bg-[#2E9D96]/10 text-[#2E9D96] flex items-center justify-center shrink-0">
                      <Phone size={15} />
                    </div>
                    <div className="leading-tight">
                      <p className="text-[13px] text-slate-500">{h.name}</p>
                      <p className="text-[15px] font-semibold text-slate-800">{h.number}</p>
                    </div>
                  </div>
                );
                return href ? (
                  <a key={h.name} href={href} data-testid={`crisis-call-${h.name.replace(/\s+/g, "-").toLowerCase()}`}>
                    {inner}
                  </a>
                ) : (
                  <div key={h.name}>{inner}</div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-6 pt-5 border-t border-slate-100">
          {anonymous ? (
            <>
              <button
                onClick={onSignup}
                data-testid="crisis-anon-signup-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#2E9D96] hover:bg-[#1F7B75] text-white font-semibold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-sm"
              >
                <HeartHandshake size={17} />
                Create a free account to turn on your safety net
              </button>
              <p className="text-xs text-slate-400 mt-2.5">
                It takes 20 seconds and is always free. Then Zaf can gently alert someone you trust if you ever go quiet.
              </p>
            </>
          ) : notified ? (
            <div className="flex items-center gap-2 text-[#1F7B75] font-semibold text-[15px]" data-testid="crisis-notified">
              <Check size={18} /> I've let your trusted person know you need them.
            </div>
          ) : (
            <>
              <button
                onClick={notifyContact}
                disabled={notifying}
                data-testid="crisis-notify-contact-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#2E9D96] hover:bg-[#1F7B75] disabled:opacity-60 text-white font-semibold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-sm"
              >
                <HeartHandshake size={17} />
                {notifying ? "Reaching out…" : "Let my trusted person know I need them"}
              </button>
              <p className="text-xs text-slate-400 mt-2.5">
                They'll get a short message asking them to check in on you — nothing from our conversation is shared.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
